import { useMemo } from 'react';
import { useSearchingStore } from '../store/useSearchStore';
import { buildSkipConditions } from '../utils/skip-logic';
import { useMissionsSearch } from './useMissionsSearch';
import { useProgrammesSearch } from './useProgrammesSearch';
import { useCoursesSearch } from './useCoursesSearch';
import { useResourcesSearch } from './useResourcesSearch';
import { useFetchMore } from './useFetchMore';
import {
  canFetchMoreMissions,
  canFetchMoreCourses,
  canFetchMoreProgrammes,
  canFetchMoreResources,
} from '../utils/pagination-helper';

export const useList = () => {
  const filtersSelected = useSearchingStore((state) => state.filters);
  const fixedTypes = useSearchingStore((state) => state.fixedTypes);

  const skipConditions = useMemo(() => buildSkipConditions(filtersSelected, fixedTypes), [filtersSelected, fixedTypes]);
  const missionsSearch = useMissionsSearch(filtersSelected, skipConditions.missions || skipConditions.queries);
  const programmesSearch = useProgrammesSearch(filtersSelected, skipConditions.programmes || skipConditions.queries);
  const coursesSearch = useCoursesSearch(filtersSelected, skipConditions.courses || skipConditions.queries);
  const resourcesSearch = useResourcesSearch(filtersSelected, skipConditions.resources || skipConditions.queries);

  const { onFetchMore, isLoadingMore } = useFetchMore({
    missionsSearch,
    programmesSearch,
    coursesSearch,
    resourcesSearch,
    skipConditions,
  });

  const loadingMore =
    missionsSearch.isLoading ||
    programmesSearch.isLoading ||
    resourcesSearch.isLoading ||
    coursesSearch.isLoading ||
    isLoadingMore;

  const canLoadMore = useMemo(() => {
    const canFetchMissions =
      !skipConditions.missions && !skipConditions.queries && missionsSearch.result
        ? canFetchMoreMissions(missionsSearch.result)
        : false;
    const canFetchCourses =
      !skipConditions.courses && !skipConditions.queries && coursesSearch.result
        ? canFetchMoreCourses(coursesSearch.result)
        : false;
    const canFetchProgrammes =
      !skipConditions.programmes && !skipConditions.queries && programmesSearch.result
        ? canFetchMoreProgrammes(programmesSearch.result)
        : false;
    const canFetchResources =
      !skipConditions.resources && !skipConditions.queries && resourcesSearch.result
        ? canFetchMoreResources(resourcesSearch.result)
        : false;

    return canFetchMissions || canFetchCourses || canFetchProgrammes || canFetchResources;
  }, [skipConditions, missionsSearch.result, coursesSearch.result, programmesSearch.result, resourcesSearch.result]);

  return {
    coursesList: coursesSearch.coursesList,
    resourcesList: resourcesSearch.resourcesList,
    programmesList: programmesSearch.programmesList,
    missionsList: missionsSearch.missionsList,
    loadingMore,
    onFetchMore,
    canLoadMore,
    errors: [missionsSearch.error].filter(Boolean),
  };
};
